import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ProjectCard from './ProjectCard';
import AddProjectModal from './AddProjectModal';
import LogsViewer from './LogsViewer';
import CommitsViewer from './CommitsViewer';
import ModalSenhaDeploy from './ModalSenhaDeploy';
import BackupsViewer from './BackupsViewer';
import { useWebSocket } from '../hooks/useWebSocket';
import { useApi } from '../hooks/useApi';
import { useLarguraTela } from '../hooks/useLarguraTela';

const TYPE_INFO = {
  aspnet: { label: '.NET',   color: '#6d28d9', bg: '#f3e8ff' },
  react:  { label: 'React',  color: '#0369a1', bg: '#e0f2fe' },
  node:   { label: 'Node',   color: '#15803d', bg: '#dcfce7' },
};

const PALETA_CORES_GRUPO = [
  '#e11d48',
  '#7c3aed',
  '#0ea5e9',
  '#16a34a',
  '#ea580c',
  '#0891b2',
  '#d97706',
  '#db2777',
  '#4f46e5',
  '#65a30d',
];

function corDoGrupo(nome) {
  const str = String(nome || '');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return PALETA_CORES_GRUPO[hash % PALETA_CORES_GRUPO.length];
}

const STATUS_DOT_COLOR = {
  idle:      '#cbd5e1',
  deploying: '#f59e0b',
  success:   '#22c55e',
  failed:    '#ef4444',
};

const GIT_UPDATE_CONFIG = {
  updated: { label: 'Atualizado', color: '#15803d', bg: '#f0fdf4', border: '#86efac' },
  outdated: { label: 'Atrasado', color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  never_deployed: { label: 'Sem atualização', color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
  stale: { label: 'Verificação atrasada', color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
  unknown: { label: 'Não verificado', color: '#64748b', bg: '#f8fafc', border: '#cbd5e1' },
  error: { label: 'Erro', color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
};

function obterConfigAtualizacao(gitUpdate) {
  if (gitUpdate?.error) return GIT_UPDATE_CONFIG.error;
  if (gitUpdate?.commitInfoStale) return GIT_UPDATE_CONFIG.stale;
  return GIT_UPDATE_CONFIG[gitUpdate?.status] || GIT_UPDATE_CONFIG.unknown;
}

function formatarDataHora(valor) {
  if (!valor) return '-';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '-';
  return data.toLocaleString('pt-BR');
}

function projetoDeveVerificarAtualizacao(projeto) {
  return projeto?.verificarAtualizacao === true;
}

function compararProjetos(projetoA, projetoB) {
  const verificarA = projetoDeveVerificarAtualizacao(projetoA) ? 0 : 1;
  const verificarB = projetoDeveVerificarAtualizacao(projetoB) ? 0 : 1;

  if (verificarA !== verificarB) return verificarA - verificarB;

  const grupoA = String(projetoA.groupName || '').trim();
  const grupoB = String(projetoB.groupName || '').trim();
  const comparacaoGrupo = grupoA.localeCompare(grupoB, 'pt-BR', { sensitivity: 'base' });

  if (comparacaoGrupo !== 0) return comparacaoGrupo;

  const nomeA = String(projetoA.name || '').trim();
  const nomeB = String(projetoB.name || '').trim();
  return nomeA.localeCompare(nomeB, 'pt-BR', { sensitivity: 'base' });
}

function aguardar(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function obterIconeVerificacao(status) {
  if (status === 'sucesso') return '✅';
  if (status === 'erro') return '❌';
  if (status === 'andamento') return '🔄';
  return '⏳';
}

const S = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    height: '100dvh',
    overflow: 'hidden',
    background: '#f4f7fb',
  },
  header: {
    background: '#ffffff',
    borderBottom: '1px solid #dbe4ef',
    padding: '0 24px',
    height: 56,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
    zIndex: 10,
  },
  logoArea: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
  },
  logoText: {
    color: '#0f172a',
    fontSize: 17,
    fontWeight: 700,
    letterSpacing: '-0.3px',
  },
  logoSub: {
    color: '#64748b',
    fontSize: 12,
  },
  usuarioInfo: {
    color: '#64748b',
    fontSize: 12,
    fontWeight: 500,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  btnSecondary: {
    padding: '7px 16px',
    background: 'transparent',
    color: '#334155',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    fontSize: 13,
    cursor: 'pointer',
  },
  body: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },

  /* Sidebar */
  sidebar: {
    width: 248,
    flexShrink: 0,
    background: '#f8fafc',
    borderRight: '1px solid #dbe4ef',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  sidebarStats: {
    padding: '14px 14px 12px',
    display: 'flex',
    gap: 8,
    borderBottom: '1px solid #dbe4ef',
  },
  statBox: {
    flex: 1,
    background: '#ffffff',
    border: '1px solid #dbe4ef',
    borderRadius: 8,
    padding: '8px 10px',
    textAlign: 'center',
  },
  statNum: {
    fontSize: 20,
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.2,
  },
  statLbl: {
    fontSize: 10,
    color: '#64748b',
    marginTop: 3,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  sidebarHeader: {
    padding: '14px 16px 6px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sidebarTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  addIconBtn: {
    background: 'none',
    border: 'none',
    color: '#3b82f6',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    padding: '0 2px',
  },
  groupContainer: {
    margin: '4px 10px 8px',
    border: '1px solid #dbe4ef',
    borderRadius: 8,
    background: '#ffffff',
    flexShrink: 0,
  },
  groupName: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    padding: '8px 12px 8px 10px',
    background: '#f8fafc',
    borderBottom: '1px solid #dbe4ef',
    borderRadius: '8px 8px 0 0',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  groupRefreshBtn: {
    marginLeft: 'auto',
    width: 24,
    height: 24,
    padding: 0,
    background: '#ffffff',
    color: '#2563eb',
    border: '1px solid #bfdbfe',
    borderRadius: 6,
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 0,
    lineHeight: 1,
    textTransform: 'none',
    textAlign: 'center',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  groupProjects: {
    padding: '4px 0',
  },
  projectItem: {
    padding: '9px 12px 9px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    borderLeft: '3px solid transparent',
    transition: 'background 0.15s',
  },
  projectItemSelected: {
    padding: '9px 12px 9px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    borderLeft: '3px solid #3b82f6',
    background: '#eff6ff',
  },
  projectDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: 4,
  },
  projectName: {
    fontSize: 13,
    fontWeight: 500,
    color: '#0f172a',
    wordBreak: 'break-word',
  },
  projectInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
  },
  projectLastSuccess: {
    fontSize: 10,
    color: '#64748b',
    marginTop: 2,
    wordBreak: 'break-word',
  },
  typeBadge: {
    fontSize: 10,
    padding: '2px 6px',
    borderRadius: 4,
    fontWeight: 600,
    flexShrink: 0,
  },
  updateBadge: {
    fontSize: 10,
    padding: '2px 6px',
    borderRadius: 4,
    fontWeight: 700,
    flexShrink: 0,
  },
  verificationList: {
    width: '100%',
    maxHeight: 220,
    overflowY: 'auto',
    border: '1px solid #dbe4ef',
    borderRadius: 8,
    background: '#f8fafc',
  },
  verificationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    borderBottom: '1px solid #e2e8f0',
    fontSize: 13,
    color: '#0f172a',
  },
  verificationIcon: {
    width: 22,
    flexShrink: 0,
    textAlign: 'center',
    fontSize: 16,
  },

  /* Main */
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#ffffff',
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748b',
    gap: 10,
    fontSize: 14,
  },
  emptyIcon: {
    fontSize: 40,
    marginBottom: 6,
    opacity: 0.4,
  },
};

export default function Dashboard({ token, usuario, onLogout }) {
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [abaMobile, setAbaMobile] = useState('projetos');
  const [showAddModal, setShowAddModal] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [showLogsViewer, setShowLogsViewer] = useState(false);
  const [showBackupsViewer, setShowBackupsViewer] = useState(false);
  const [projectIdHistorico, setProjectIdHistorico] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verificandoAtualizacoes, setVerificandoAtualizacoes] = useState(false);
  const [projetoAtualizandoStatusId, setProjetoAtualizandoStatusId] = useState(null);
  const [grupoAtualizandoStatusNome, setGrupoAtualizandoStatusNome] = useState(null);
  const [statusVerificacaoProjetos, setStatusVerificacaoProjetos] = useState({});
  const [ultimaVerificacaoAtualizacao, setUltimaVerificacaoAtualizacao] = useState(null);
  const [projetoIniciandoDeployId, setProjetoIniciandoDeployId] = useState(null);
  const [modalCommits, setModalCommits] = useState(null); // { projectId, projectName }
  const [modalSenhaDeploy, setModalSenhaDeploy] = useState(null); // { projectId, projectName }
  const [erroSenhaDeploy, setErroSenhaDeploy] = useState('');
  const { logs, statuses, clearLogs } = useWebSocket(token);
  const api = useApi(token);
  const larguraTela = useLarguraTela();
  const ehMobile = larguraTela <= 900;
  const disparoAutomaticoRef = useRef(false);
  const verificacaoInicialExecutadaRef = useRef(false);
  const statusAnteriorProjetosRef = useRef({});
  const projetosAtuaisRef = useRef([]);
  const parametrosDeployUrl = useMemo(() => {
    const parametros = new URLSearchParams(window.location.search);
    const projetoId = (parametros.get('projeto') || '').trim();
    const senhaInformada = parametros.has('senha') ? parametros.get('senha') : null;

    return {
      projetoId,
      senhaInformada,
    };
  }, []);

  const projetosOrdenados = useMemo(() => {
    return [...projects].sort(compararProjetos);
  }, [projects]);

  const projetosParaVerificacao = useMemo(() => {
    return projetosOrdenados.filter(projetoDeveVerificarAtualizacao);
  }, [projetosOrdenados]);

  useEffect(() => {
    projetosAtuaisRef.current = projects;
  }, [projects]);

  const gruposProjetos = useMemo(() => {
    const grupos = [];
    let ultimoGrupo = '';

    projetosOrdenados.forEach((projeto) => {
      const nomeGrupo = String(projeto.groupName || 'Sem grupo').trim() || 'Sem grupo';
      const grupoExistente = grupos[grupos.length - 1];

      if (!grupoExistente || ultimoGrupo !== nomeGrupo) {
        grupos.push({ nome: nomeGrupo, projetos: [projeto] });
      } else {
        grupoExistente.projetos.push(projeto);
      }

      ultimoGrupo = nomeGrupo;
    });

    return grupos;
  }, [projetosOrdenados]);

  const mesclarStatusAtualizacao = useCallback((resultados = []) => {
    const statusPorProjeto = new Map(resultados.map((item) => [item.projectId, item]));

    setProjects((projetosAtuais) => projetosAtuais.map((projeto) => ({
      ...projeto,
      gitUpdate: statusPorProjeto.has(projeto.id)
        ? statusPorProjeto.get(projeto.id)
        : projeto.gitUpdate,
    })));
  }, []);

  const verificarAtualizacoesLegado = useCallback(async ({ silencioso = false } = {}) => {
    try {
      setVerificandoAtualizacoes(true);
      const dados = await api.getProjectsUpdateStatus();
      mesclarStatusAtualizacao(dados.projects || []);
      setUltimaVerificacaoAtualizacao(dados.checkedAt || new Date().toISOString());
    } catch (err) {
      console.error('Erro ao verificar atualizações:', err);
      if (!silencioso) alert(`Erro ao verificar atualizações: ${err.message}`);
    } finally {
      setVerificandoAtualizacoes(false);
    }
  }, [api, mesclarStatusAtualizacao]);

  void verificarAtualizacoesLegado;

  const verificarAtualizacoes = useCallback(async ({ silencioso = false, projetosBase = null } = {}) => {
    const projetosMarcados = [...(projetosBase || projetosAtuaisRef.current)]
      .filter(projetoDeveVerificarAtualizacao)
      .sort(compararProjetos);
    const statusInicial = Object.fromEntries(projetosMarcados.map((projeto) => [projeto.id, 'pendente']));
    const erros = [];

    try {
      setVerificandoAtualizacoes(true);
      setStatusVerificacaoProjetos(statusInicial);

      for (const projeto of projetosMarcados) {
        setStatusVerificacaoProjetos((statusAtual) => ({
          ...statusAtual,
          [projeto.id]: 'andamento',
        }));

        try {
          const dados = await api.getProjectUpdateStatus(projeto.id);
          const resultados = dados.projects || [];
          mesclarStatusAtualizacao(resultados);

          const resultadoProjeto = resultados.find((item) => item.projectId === projeto.id);
          setStatusVerificacaoProjetos((statusAtual) => ({
            ...statusAtual,
            [projeto.id]: resultadoProjeto?.error ? 'erro' : 'sucesso',
          }));
        } catch (err) {
          erros.push(`${projeto.name}: ${err.message}`);
          setStatusVerificacaoProjetos((statusAtual) => ({
            ...statusAtual,
            [projeto.id]: 'erro',
          }));
        }
      }

      setUltimaVerificacaoAtualizacao(new Date().toISOString());
      if (erros.length && !silencioso) {
        alert(`Erro ao verificar atualizações:\n${erros.join('\n')}`);
      }
      await aguardar(700);
    } catch (err) {
      console.error('Erro ao verificar atualizações:', err);
      if (!silencioso) alert(`Erro ao verificar atualizações: ${err.message}`);
    } finally {
      setVerificandoAtualizacoes(false);
    }
  }, [api, mesclarStatusAtualizacao]);

  const atualizarStatusProjeto = useCallback(async (projectId, { silencioso = false } = {}) => {
    if (!projectId) return;

    try {
      setProjetoAtualizandoStatusId(projectId);
      const dados = await api.getProjectUpdateStatus(projectId);
      mesclarStatusAtualizacao(dados.projects || []);
      setUltimaVerificacaoAtualizacao(dados.checkedAt || new Date().toISOString());
    } catch (err) {
      console.error('Erro ao atualizar status do projeto:', err);
      if (!silencioso) alert(`Erro ao atualizar status: ${err.message}`);
    } finally {
      setProjetoAtualizandoStatusId(null);
    }
  }, [api, mesclarStatusAtualizacao]);

  const atualizarStatusGrupo = useCallback(async (grupo) => {
    const projetosDoGrupo = Array.isArray(grupo?.projetos) ? grupo.projetos : [];
    if (!grupo?.nome || projetosDoGrupo.length === 0) return;

    const erros = [];

    try {
      setGrupoAtualizandoStatusNome(grupo.nome);

      for (const projeto of projetosDoGrupo) {
        try {
          const dados = await api.getProjectUpdateStatus(projeto.id);
          mesclarStatusAtualizacao(dados.projects || []);
        } catch (err) {
          erros.push(`${projeto.name}: ${err.message}`);
        }
      }

      setUltimaVerificacaoAtualizacao(new Date().toISOString());
      if (erros.length) {
        alert(`Erro ao atualizar alguns projetos:\n${erros.join('\n')}`);
      }
    } finally {
      setGrupoAtualizandoStatusNome(null);
    }
  }, [api, mesclarStatusAtualizacao]);

  const loadProjects = useCallback(async () => {
    try {
      const data = await api.getProjects();
      setProjects((projetosAtuais) => {
        const statusAtualPorProjeto = new Map(projetosAtuais.map((projeto) => [projeto.id, projeto.gitUpdate]));
        return data.map((projeto) => ({
          ...projeto,
          gitUpdate: statusAtualPorProjeto.get(projeto.id)
            || projeto.gitUpdate,
        }));
      });
      return data;
    } catch (err) {
      console.error('Erro ao carregar projetos:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    let cancelado = false;

    async function carregarTela() {
      const data = await loadProjects();
      if (cancelado || verificacaoInicialExecutadaRef.current) return;

      verificacaoInicialExecutadaRef.current = true;
      await verificarAtualizacoes({ silencioso: true, projetosBase: data });
    }

    carregarTela();

    return () => {
      cancelado = true;
    };
  }, [loadProjects, verificarAtualizacoes]);

  const statusAnteriorProjetoSelecionadoRef = useRef(null);

  useEffect(() => {
    if (!selectedId) {
      statusAnteriorProjetoSelecionadoRef.current = null;
      return;
    }
    const statusAtual = statuses[selectedId];
    const statusAnterior = statusAnteriorProjetoSelecionadoRef.current;

    if (statusAnterior === 'deploying' && statusAtual === 'success') {
      setSelectedId(null);
    }

    if (statusAtual !== undefined) {
      statusAnteriorProjetoSelecionadoRef.current = statusAtual;
    }
  }, [selectedId, statuses]);

  useEffect(() => {
    if (!ehMobile) {
      setAbaMobile('projetos');
      return;
    }

    if (selectedId) {
      setAbaMobile('detalhes');
    } else {
      setAbaMobile('projetos');
    }
  }, [ehMobile, selectedId]);

  const resolveStatus = useCallback((project) => {
    const statusWs = statuses[project.id];
    if (statusWs) return statusWs;

    if (project.id === projetoIniciandoDeployId) {
      const statusProjeto = project.deployStatus || 'idle';
      if (statusProjeto === 'success' || statusProjeto === 'failed') return statusProjeto;
      return 'deploying';
    }

    return project.deployStatus || 'idle';
  }, [statuses, projetoIniciandoDeployId]);

  useEffect(() => {
    if (!projetoIniciandoDeployId) return;

    const projeto = projects.find((item) => item.id === projetoIniciandoDeployId);
    const statusAtual = statuses[projetoIniciandoDeployId] || projeto?.deployStatus || 'idle';
    if (statusAtual !== 'idle') {
      setProjetoIniciandoDeployId(null);
    }
  }, [projetoIniciandoDeployId, projects, statuses]);

  useEffect(() => {
    Object.entries(statuses).forEach(([projectId, statusAtual]) => {
      const statusAnterior = statusAnteriorProjetosRef.current[projectId];

      if (statusAnterior === 'deploying' && statusAtual === 'success') {
        void atualizarStatusProjeto(projectId, { silencioso: true });
      }
    });

    statusAnteriorProjetosRef.current = { ...statuses };
  }, [atualizarStatusProjeto, statuses]);

  const deployingCount = projetosOrdenados.filter((project) => resolveStatus(project) === 'deploying').length;
  const interfaceBloqueada = Boolean(projetoIniciandoDeployId) || deployingCount > 0;
  const acoesDesabilitadas = interfaceBloqueada || verificandoAtualizacoes || Boolean(grupoAtualizandoStatusNome);
  const selectedProject = projetosOrdenados.find((p) => p.id === selectedId);
  const usuarioAdmin = usuario?.admin === true;
  const usuarioReadOnly = usuario?.readOnly === true;
  const nomeUsuarioLogado = usuario?.nomeCompleto || usuario?.login || 'Usuário';

  const projetoEhSomenteleitura = (projeto) => {
    if (usuarioReadOnly) return true;
    const lista = Array.isArray(usuario?.projetos_somenteleitura) ? usuario.projetos_somenteleitura : [];
    return lista.includes(projeto?.id);
  };

  const executarDeploy = useCallback(async (projectId, senha = null) => {
    try {
      setProjetoIniciandoDeployId(projectId);
      clearLogs(projectId);
      await api.triggerDeploy(projectId, senha);
    } catch (err) {
      setProjetoIniciandoDeployId(null);
      if (senha !== null) {
        const projeto = projetosOrdenados.find((p) => p.id === projectId);
        setErroSenhaDeploy(err.message);
        setModalSenhaDeploy({ projectId, projectName: projeto?.name || '' });
      } else {
        alert(`Erro: ${err.message}`);
      }
    }
  }, [api, clearLogs, projetosOrdenados]);

  const handleDeploy = useCallback(async (projectId) => {
    if (acoesDesabilitadas) return;
    const projeto = projetosOrdenados.find((p) => p.id === projectId);
    if (projeto?.temSenha) {
      setErroSenhaDeploy('');
      setModalSenhaDeploy({ projectId, projectName: projeto.name });
      return;
    }
    await executarDeploy(projectId, null);
  }, [acoesDesabilitadas, projetosOrdenados, executarDeploy]);

  const handleConfirmarSenhaDeploy = useCallback(async (senha) => {
    const { projectId } = modalSenhaDeploy;
    setModalSenhaDeploy(null);
    setErroSenhaDeploy('');
    await executarDeploy(projectId, senha);
  }, [modalSenhaDeploy, executarDeploy]);

  useEffect(() => {
    if (disparoAutomaticoRef.current) return;
    if (!parametrosDeployUrl.projetoId) return;
    if (loading || acoesDesabilitadas) return;

    const projeto = projetosOrdenados.find((item) => item.id === parametrosDeployUrl.projetoId);
    if (!projeto) {
      disparoAutomaticoRef.current = true;
      alert(`Projeto "${parametrosDeployUrl.projetoId}" não encontrado ou sem permissão.`);
      return;
    }

    setSelectedId(projeto.id);

    if (!projeto.enabled) {
      disparoAutomaticoRef.current = true;
      alert(`Projeto "${projeto.name}" está desabilitado.`);
      return;
    }

    if (projeto.temSenha && !parametrosDeployUrl.senhaInformada) {
      disparoAutomaticoRef.current = true;
      alert(
        `O projeto "${projeto.name}" exige senha. Informe também na URL: ?projeto=${projeto.id}&senha=SUA_SENHA`
      );
      return;
    }

    disparoAutomaticoRef.current = true;
    void executarDeploy(projeto.id, projeto.temSenha ? parametrosDeployUrl.senhaInformada : null);
  }, [
    acoesDesabilitadas,
    executarDeploy,
    loading,
    parametrosDeployUrl,
    projetosOrdenados,
  ]);

  const handleSaveProject = async (project) => {
    if (acoesDesabilitadas) return;

    try {
      if (projectToEdit) {
        await api.updateProject(projectToEdit.id, project);
      } else {
        await api.createProject(project);
      }
      setShowAddModal(false);
      setProjectToEdit(null);
      await loadProjects();
    } catch (err) {
      alert(`Erro: ${err.message}`);
    }
  };

  const handleOpenEditModal = (project) => {
    if (acoesDesabilitadas) return;
    setProjectToEdit(project);
    setShowAddModal(true);
  };

  const handleCloseProjectModal = () => {
    if (acoesDesabilitadas) return;
    setShowAddModal(false);
    setProjectToEdit(null);
  };

  const handleQuickBranchUpdate = async (project) => {
    if (acoesDesabilitadas) return;

    const branchAtual = project.branch || 'main';
    const novoBranch = window.prompt(`Informe o novo branch para "${project.name}":`, branchAtual);
    if (novoBranch === null) return;
    const branchTratado = novoBranch.trim();
    if (!branchTratado) { alert('Branch não pode ficar vazio.'); return; }
    try {
      await api.updateProject(project.id, { branch: branchTratado });
      await loadProjects();
    } catch (err) {
      alert(`Erro ao atualizar branch: ${err.message}`);
    }
  };

  const handleOpenHistoricoGeral = () => {
    if (acoesDesabilitadas) return;
    setProjectIdHistorico(null);
    setShowLogsViewer(true);
  };

  const handleOpenHistoricoProjeto = (project) => {
    if (acoesDesabilitadas) return;
    setProjectIdHistorico(project.id);
    setShowLogsViewer(true);
  };

  const handleOpenCommits = (project) => {
    if (acoesDesabilitadas) return;
    setModalCommits({ projectId: project.id, projectName: project.name });
  };

  return (
    <div style={S.root}>
      <style>{`
        @keyframes cd-spin { to { transform: rotate(360deg); } }
        @keyframes cd-fade-in { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
        .cd-spinner {
          width: 48px; height: 48px;
          border: 4px solid #dbe4ef;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: cd-spin 0.8s linear infinite;
        }
      `}</style>

      {(loading || verificandoAtualizacoes) && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(15,23,42,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(2px)',
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 16,
            padding: '36px 48px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
            boxShadow: '0 20px 60px rgba(15,23,42,0.25)',
            animation: 'cd-fade-in 0.2s ease-out',
            minWidth: 320,
            maxWidth: 'calc(100vw - 32px)',
          }}>
            <div className="cd-spinner" />
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', textAlign: 'center' }}>
              {loading ? 'Carregando projetos' : 'Verificando atualizações'}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', textAlign: 'center' }}>
              {loading ? 'Aguarde um instante...' : 'Consultando status de cada projeto...'}
            </div>
            {verificandoAtualizacoes && (
              <div style={S.verificationList}>
                {projetosParaVerificacao.length === 0 ? (
                  <div style={{ ...S.verificationItem, borderBottom: 'none', color: '#64748b' }}>
                    Nenhum projeto marcado para verificação.
                  </div>
                ) : (
                  projetosParaVerificacao.map((projeto, index) => (
                    <div
                      key={projeto.id}
                      style={{
                        ...S.verificationItem,
                        borderBottom: index === projetosParaVerificacao.length - 1 ? 'none' : S.verificationItem.borderBottom,
                      }}
                    >
                      <span style={S.verificationIcon}>
                        {obterIconeVerificacao(statusVerificacaoProjetos[projeto.id])}
                      </span>
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {projeto.groupName ? `${projeto.groupName} - ${projeto.name}` : projeto.name}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Header */}
      <div
        style={{
          ...S.header,
          padding: ehMobile ? '10px 12px' : S.header.padding,
          height: ehMobile ? 'auto' : S.header.height,
          gap: ehMobile ? 8 : 0,
          flexWrap: ehMobile ? 'wrap' : 'nowrap',
          alignItems: ehMobile ? 'flex-start' : 'center',
        }}
      >
        <div style={{ ...S.logoArea, flexWrap: 'wrap', rowGap: 2 }}>
          <span style={S.logoText}>Atualizador HeadThink!</span>
          <span style={S.logoSub}>Deploy Atualizações</span>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 5,
            background: '#f1f5f9', border: '1px solid #dbe4ef',
            borderRadius: 20, padding: '3px 10px 3px 4px', marginTop: 2,
          }}>
            <div style={{
              width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
              background: usuarioAdmin ? '#7c3aed' : usuarioReadOnly ? '#d97706' : '#3b82f6',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 800,
            }}>
              {nomeUsuarioLogado.charAt(0).toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>
              {nomeUsuarioLogado}
            </span>
            {usuarioAdmin && (
              <span style={{
                fontSize: 9, fontWeight: 700, color: '#6d28d9',
                background: '#ede9fe', border: '1px solid #c4b5fd',
                borderRadius: 4, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>Admin</span>
            )}
            {usuarioReadOnly && (
              <span style={{
                fontSize: 9, fontWeight: 700, color: '#92400e',
                background: '#fef3c7', border: '1px solid #fcd34d',
                borderRadius: 4, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>Leitura</span>
            )}
          </div>
        </div>
        <div
          style={{
            ...S.headerRight,
            width: ehMobile ? '100%' : 'auto',
            justifyContent: ehMobile ? 'space-between' : 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <button
            style={{
              ...S.btnSecondary,
              flex: ehMobile ? 1 : 'auto',
              minHeight: ehMobile ? 44 : 'auto',
              padding: ehMobile ? '13px 6px' : S.btnSecondary.padding,
              fontSize: ehMobile ? 12 : S.btnSecondary.fontSize,
              opacity: acoesDesabilitadas ? 0.55 : 1,
              cursor: acoesDesabilitadas ? 'not-allowed' : S.btnSecondary.cursor,
            }}
            onClick={() => verificarAtualizacoes()}
            disabled={acoesDesabilitadas}
            title={ultimaVerificacaoAtualizacao ? `Última verificação: ${formatarDataHora(ultimaVerificacaoAtualizacao)}` : 'Verificar atualizações'}
          >
            {verificandoAtualizacoes ? 'Buscando...' : 'Atualizar'}
          </button>
          <button
            style={{
              ...S.btnSecondary,
              flex: ehMobile ? 1 : 'auto',
              minHeight: ehMobile ? 44 : 'auto',
              padding: ehMobile ? '13px 6px' : S.btnSecondary.padding,
              fontSize: ehMobile ? 12 : S.btnSecondary.fontSize,
              opacity: acoesDesabilitadas ? 0.55 : 1,
              cursor: acoesDesabilitadas ? 'not-allowed' : S.btnSecondary.cursor,
            }}
            onClick={handleOpenHistoricoGeral}
            disabled={acoesDesabilitadas}
          >
            Histórico
          </button>
          <button
            style={{
              ...S.btnSecondary,
              flex: ehMobile ? 1 : 'auto',
              minHeight: ehMobile ? 44 : 'auto',
              padding: ehMobile ? '13px 6px' : S.btnSecondary.padding,
              fontSize: ehMobile ? 12 : S.btnSecondary.fontSize,
              opacity: acoesDesabilitadas ? 0.55 : 1,
              cursor: acoesDesabilitadas ? 'not-allowed' : S.btnSecondary.cursor,
            }}
            onClick={() => { if (!acoesDesabilitadas) setShowBackupsViewer(true); }}
            disabled={acoesDesabilitadas}
            title="Auditoria de backups do SQL Server"
          >
            Backups DB
          </button>
          <button
            style={{
              ...S.btnSecondary,
              flex: ehMobile ? 1 : 'auto',
              minHeight: ehMobile ? 44 : 'auto',
              padding: ehMobile ? '13px 6px' : S.btnSecondary.padding,
              fontSize: ehMobile ? 12 : S.btnSecondary.fontSize,
              opacity: acoesDesabilitadas ? 0.55 : 1,
              cursor: acoesDesabilitadas ? 'not-allowed' : S.btnSecondary.cursor,
            }}
            onClick={onLogout}
            disabled={acoesDesabilitadas}
          >
            Sair
          </button>
        </div>
      </div>

      <div style={{ ...S.body, flexDirection: ehMobile ? 'column' : 'row' }}>
        {ehMobile && (
          <div
            style={{
              display: 'flex',
              gap: 0,
              borderBottom: '1px solid #dbe4ef',
              background: '#ffffff',
              flexShrink: 0,
            }}
          >
            <button
              style={{
                flex: 1,
                minHeight: 44,
                padding: '13px 8px',
                background: 'none',
                border: 'none',
                borderBottom: abaMobile === 'projetos' ? '2px solid #3b82f6' : '2px solid transparent',
                color: abaMobile === 'projetos' ? '#2563eb' : '#64748b',
                fontWeight: abaMobile === 'projetos' ? 700 : 500,
                fontSize: 13,
                cursor: acoesDesabilitadas ? 'not-allowed' : 'pointer',
                opacity: acoesDesabilitadas ? 0.55 : 1,
              }}
              onClick={() => setAbaMobile('projetos')}
              disabled={acoesDesabilitadas}
            >
              Projetos
            </button>
            <button
              style={{
                flex: 1,
                minHeight: 44,
                padding: '13px 8px',
                background: 'none',
                border: 'none',
                borderBottom: abaMobile === 'detalhes' ? '2px solid #3b82f6' : '2px solid transparent',
                color: abaMobile === 'detalhes' ? '#2563eb' : '#64748b',
                fontWeight: abaMobile === 'detalhes' ? 700 : 500,
                fontSize: 13,
                cursor: acoesDesabilitadas ? 'not-allowed' : 'pointer',
                opacity: acoesDesabilitadas ? 0.55 : 1,
              }}
              onClick={() => setAbaMobile('detalhes')}
              disabled={acoesDesabilitadas}
            >
              Detalhes
            </button>
          </div>
        )}

        {/* Sidebar */}
        <div
          style={{
            ...S.sidebar,
            width: ehMobile ? '100%' : S.sidebar.width,
            maxHeight: ehMobile ? '100%' : '100%',
            borderRight: ehMobile ? 'none' : S.sidebar.borderRight,
            borderBottom: ehMobile ? '1px solid #dbe4ef' : 'none',
            display: ehMobile && abaMobile !== 'projetos' ? 'none' : 'flex',
            flex: ehMobile ? 1 : 'none',
          }}
        >
          <div style={{ ...S.sidebarStats, padding: ehMobile ? '10px 10px 8px' : S.sidebarStats.padding }}>
            <div style={S.statBox}>
              <div style={S.statNum}>{projects.length}</div>
              <div style={S.statLbl}>Projetos</div>
            </div>
            <div style={S.statBox}>
              <div style={{ ...S.statNum, color: deployingCount > 0 ? '#d97706' : '#16a34a' }}>
                {deployingCount}
              </div>
              <div style={S.statLbl}>Em deploy</div>
            </div>
          </div>

          <div style={{ ...S.sidebarHeader, padding: ehMobile ? '10px 12px 6px' : S.sidebarHeader.padding }}>
            <span style={S.sidebarTitle}>Projetos</span>
            {!usuarioReadOnly && (
              <button
                style={{
                  ...S.addIconBtn,
                  opacity: acoesDesabilitadas ? 0.5 : 1,
                  cursor: acoesDesabilitadas ? 'not-allowed' : S.addIconBtn.cursor,
                }}
                onClick={() => { setProjectToEdit(null); setShowAddModal(true); }}
                title="Adicionar projeto"
                disabled={acoesDesabilitadas}
              >
                +
              </button>
            )}
          </div>

          {loading ? (
            <div style={{ padding: '16px', color: '#64748b', fontSize: 13 }}>Carregando...</div>
          ) : projetosOrdenados.length === 0 ? (
            <div style={{ padding: '16px' }} />
          ) : (
            gruposProjetos.map((grupo, indexGrupo) => (
              <div key={`${grupo.nome}-${indexGrupo}`} style={{ ...S.groupContainer, margin: ehMobile ? '4px 8px 8px' : S.groupContainer.margin }}>
                <div style={S.groupName}>
                  <span style={{ width: 3, height: 12, borderRadius: 2, background: corDoGrupo(grupo.nome), flexShrink: 0, display: 'inline-block' }} />
                  <span style={{ color: corDoGrupo(grupo.nome) }}>{grupo.nome}</span>
                  <button
                    style={{
                      ...S.groupRefreshBtn,
                      opacity: acoesDesabilitadas ? 0.55 : 1,
                      cursor: acoesDesabilitadas ? 'not-allowed' : S.groupRefreshBtn.cursor,
                    }}
                    onClick={() => atualizarStatusGrupo(grupo)}
                    disabled={acoesDesabilitadas}
                    title="Atualizar status"
                  >
                    {grupoAtualizandoStatusNome === grupo.nome ? '...' : '⟳'}
                  </button>
                </div>
                <div style={S.groupProjects}>
                  {grupo.projetos.map((p) => {
                    const status = resolveStatus(p);
                    const typeInfo = TYPE_INFO[p.type] || { label: p.type, color: '#64748b', bg: '#f1f5f9' };
                    const updateInfo = obterConfigAtualizacao(p.gitUpdate);
                    const isSelected = p.id === selectedId;
                    const ehSomenteleitura = projetoEhSomenteleitura(p);
                    const ultimoBuildSucessoTexto = p.lastSuccessDeploy?.timestamp
                      ? new Date(p.lastSuccessDeploy.timestamp).toLocaleString('pt-BR')
                      : 'Nenhum sucesso';
                    return (
                      <div
                        key={p.id}
                        style={{
                          ...(isSelected ? S.projectItemSelected : S.projectItem),
                          padding: ehMobile ? '12px 10px 12px 14px' : (isSelected ? S.projectItemSelected.padding : S.projectItem.padding),
                          cursor: acoesDesabilitadas ? 'not-allowed' : (isSelected ? S.projectItemSelected.cursor : S.projectItem.cursor),
                          opacity: acoesDesabilitadas ? 0.75 : 1,
                          background: isSelected ? '#eff6ff' : ehSomenteleitura ? '#fffbeb' : undefined,
                        }}
                        onClick={() => {
                          if (acoesDesabilitadas) return;
                          setSelectedId(p.id);
                        }}
                      >
                        <div
                          className={status === 'deploying' ? 'deploying-dot' : ''}
                          style={{
                            ...S.projectDot,
                            background: STATUS_DOT_COLOR[status] || STATUS_DOT_COLOR.idle,
                          }}
                        />
                        <div style={S.projectInfo}>
                          <span style={S.projectName}>{p.name}</span>
                          <span style={S.projectLastSuccess}>
                            Último sucesso: {ultimoBuildSucessoTexto}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
                          <span
                            style={{
                              ...S.typeBadge,
                              color: typeInfo.color,
                              background: typeInfo.bg,
                              display: ehMobile ? 'none' : 'inline-flex',
                            }}
                          >
                            {typeInfo.label}
                          </span>
                          <span
                            style={{
                              ...S.updateBadge,
                              color: updateInfo.color,
                              background: updateInfo.bg,
                              border: `1px solid ${updateInfo.border}`,
                            }}
                            title={p.gitUpdate?.lastCommitAt ? `Último commit: ${formatarDataHora(p.gitUpdate.lastCommitAt)} | Verificado em: ${formatarDataHora(p.gitUpdate.lastCommitCheckedAt)}` : updateInfo.label}
                          >
                            {updateInfo.label}
                          </span>
                          {ehSomenteleitura && (
                            <span style={{
                              fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                              color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d',
                              textTransform: 'uppercase', letterSpacing: '0.04em',
                            }}>
                              Somente leitura
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Painel principal */}
        <div
          style={{
            ...S.main,
            minHeight: 0,
            display: ehMobile && abaMobile !== 'detalhes' ? 'none' : 'flex',
            flex: ehMobile ? 1 : S.main.flex,
          }}
        >
          {!selectedProject && projects.length > 0 ? (
            <div style={S.emptyState}>
              <div style={S.emptyIcon}>🚀</div>
              <div>Selecione um projeto na lista</div>
            </div>
          ) : !selectedProject ? null : (
            <ProjectCard
              key={selectedProject.id}
              project={selectedProject}
              logs={logs[selectedProject.id] || []}
              status={statuses[selectedProject.id]}
              onDeploy={projetoEhSomenteleitura(selectedProject) ? null : handleDeploy}
              onEditProject={projetoEhSomenteleitura(selectedProject) ? null : handleOpenEditModal}
              onEditBranch={projetoEhSomenteleitura(selectedProject) ? null : handleQuickBranchUpdate}
              onOpenHistory={handleOpenHistoricoProjeto}
              onOpenCommits={handleOpenCommits}
              onAtualizarStatus={atualizarStatusProjeto}
              atualizandoStatus={projetoAtualizandoStatusId === selectedProject.id}
              bloquearAcoes={acoesDesabilitadas}
              somenteLeitura={projetoEhSomenteleitura(selectedProject)}
            />
          )}
        </div>
      </div>

      {showAddModal && (
        <AddProjectModal
          api={api}
          onClose={handleCloseProjectModal}
          onSave={handleSaveProject}
          initialProject={projectToEdit}
        />
      )}

      {showLogsViewer && (
        <LogsViewer
          token={token}
          projectIdInicial={projectIdHistorico}
          onClose={() => {
            setShowLogsViewer(false);
            setProjectIdHistorico(null);
          }}
        />
      )}

      {modalCommits && (
        <CommitsViewer
          api={api}
          projectId={modalCommits.projectId}
          projectName={modalCommits.projectName}
          limit={20}
          onClose={() => setModalCommits(null)}
        />
      )}

      {showBackupsViewer && (
        <BackupsViewer
          token={token}
          onClose={() => setShowBackupsViewer(false)}
        />
      )}

      {modalSenhaDeploy && (
        <ModalSenhaDeploy
          nomeProjeto={modalSenhaDeploy.projectName}
          erroExterno={erroSenhaDeploy}
          onConfirmar={handleConfirmarSenhaDeploy}
          onCancelar={() => { setModalSenhaDeploy(null); setErroSenhaDeploy(''); }}
        />
      )}
    </div>
  );
}
